// Caminho do arquivo: src\components\common\PluginCard.tsx
import { Link, useNavigate } from "react-router-dom";
import { type Plugin } from '../../context/PluginContext';
import { useCart } from "../../context/CartContext";
import { PluginIconPlaceholder } from "./PluginIconPlaceholder";
import { APP_CONFIG } from '../../config/constants';

// Ícone de Compras
const CartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

// Ícone de Downloads
const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

interface PluginCardProps {
    plugin: Plugin;
}

export function PluginCard({ plugin }: PluginCardProps) {
    const navigate = useNavigate();
    const { addToCart, cartItems } = useCart();
    const isInCart = cartItems.some(item => item.id === plugin.id);

    const handleAddToCart = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        addToCart(plugin);
    };

    const handleMoreInfo = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        navigate(`/plugins/${plugin.slug}`);
    }

    return (
        <div className="flex flex-col bg-card-bg rounded-xl overflow-hidden shadow-subtle hover:shadow-subtle-hover hover:-translate-y-1.5 transition-all duration-300 group">
            <Link to={`/plugins/${plugin.slug}`} className="block">
                <div className="relative h-48 w-full bg-background flex items-center justify-center overflow-hidden">
                    {plugin.iconUrl ? (
                        <img
                            src={`${APP_CONFIG.uploadsBaseUrl}${plugin.iconUrl}`}
                            alt={`Ícone do plugin ${plugin.name}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                            }}
                        />
                    ) : (
                        <PluginIconPlaceholder pluginName={plugin.name} className="w-full h-full" />
                    )}

                    {/* Badge de stats no topo */}
                    <div className="absolute top-2 right-2 flex items-center gap-2 py-1 px-2.5 rounded-full bg-black/40 backdrop-blur-md text-white text-[11px] font-semibold">
                        <div className="flex items-center gap-1">
                            <CartIcon />
                            <span>{plugin.purchaseCount || 0}</span>
                        </div>
                        <div className="w-px h-3 bg-white/30"></div>
                        <div className="flex items-center gap-1">
                            <DownloadIcon />
                            <span>{plugin.downloadCount || 0}</span>
                        </div>
                    </div>
                </div>
            </Link>

            <div className="p-3 flex flex-col flex-grow">
                <p className="text-xs text-text-secondary line-clamp-2 leading-relaxed min-h-[2.5rem]">
                    {plugin.tagline}
                </p>

                {/* Preço e botão */}
                <div className="mt-3 pt-3 border-t border-border-color flex items-center justify-between gap-2">
                    <p className="text-base font-bold text-text-primary whitespace-nowrap">
                        {plugin.price === 0 ? (
                            <span className="text-green-600">Grátis</span>
                        ) : (
                            `R$ ${plugin.price.toFixed(2)}`
                        )}
                    </p>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={handleAddToCart}
                            disabled={isInCart}
                            className="bg-brand-blue text-white font-semibold px-2.5 py-1.5 rounded-lg text-xs hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            {isInCart ? 'Adicionado' : 'Comprar'}
                        </button>
                        <button
                            onClick={handleMoreInfo}
                            className="h-8 w-8 flex items-center justify-center bg-gray-100 text-text-secondary rounded-lg hover:bg-gray-200 transition-colors"
                            title="Mais Informações"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}